import { motion, useReducedMotion } from "framer-motion";
import { MaskedLine } from "@/components/Reveal";

const ease = [0.22, 1, 0.36, 1];

const Wave = ({ delay, mirrored = false, reduced }) => (
  <motion.svg
    viewBox="0 0 300 80"
    className="h-12 w-36 sm:w-48"
    preserveAspectRatio="none"
    initial={reduced ? "show" : "hidden"}
    whileInView="show"
    viewport={{ once: true, margin: "-100px" }}
    aria-hidden="true"
  >
    <motion.path
      d={mirrored ? "M 12 40 C 130 6, 210 74, 296 40" : "M 4 40 C 90 6, 170 74, 288 40"}
      fill="none"
      stroke="#4B3428"
      strokeOpacity="0.55"
      strokeWidth="1.5"
      vectorEffect="non-scaling-stroke"
      strokeLinecap="round"
      variants={{
        hidden: { pathLength: 0 },
        show: { pathLength: 1, transition: { duration: 1.1, delay, ease } },
      }}
    />
  </motion.svg>
);

const ConnectionPoint = () => {
  const reduced = useReducedMotion();
  return (
    <section
      data-chapter="02"
      data-testid="connection-point-section"
      className="relative overflow-hidden bg-sand/45 py-32 lg:py-44"
    >
      <div className="mx-auto flex max-w-[1500px] flex-col items-center px-6 lg:px-14">
        <motion.p
          className="mb-14 font-sans text-[11px] font-medium uppercase tracking-[0.4em] text-walnut/60"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          data-testid="connection-point-label"
        >
          02 — Your Safe Place
        </motion.p>

        <div className="mb-14 flex items-center gap-4" aria-hidden="true">
          <Wave delay={0.15} reduced={reduced} />
          <motion.span
            className="h-3 w-3 shrink-0 rounded-full bg-gold shadow-[0_0_18px_rgba(184,154,99,0.5)]"
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: reduced ? 0.1 : 0.8, ease: "easeOut" }}
            data-testid="connection-point-dot"
          />
          <Wave delay={reduced ? 0.1 : 0.9} mirrored reduced={reduced} />
        </div>

        <h2
          className="text-center font-serif text-5xl font-light leading-[1.05] tracking-tight text-forest sm:text-6xl lg:text-7xl"
          data-testid="safe-place-headline"
        >
          <MaskedLine delay={0.2}>This is your</MaskedLine>
          <MaskedLine delay={0.35}>
            <span className="italic text-walnut">safe place</span>
            <span className="mb-2 ml-3 inline-block h-2 w-2 rounded-full bg-accent align-baseline" />
          </MaskedLine>
        </h2>

        <motion.p
          className="mt-12 max-w-xl text-center font-sans text-base font-light leading-relaxed tracking-wide text-walnut/80"
          initial={{ opacity: 0, y: reduced ? 0 : 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: reduced ? 0.15 : 1.2, ease }}
          data-testid="connection-point-caption"
        >
          No demands, no roles, no unfinished tasks. Work and worries stay at
          the door — here it is only you, your body, and time that belongs
          entirely to you.
        </motion.p>

        <motion.span
          className="mt-16 block h-[7px] w-[7px] rounded-full bg-walnut/70"
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 0.7, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: reduced ? 0.2 : 1.6 }}
          aria-hidden="true"
        />
      </div>
    </section>
  );
};

export default ConnectionPoint;
